import React from 'react'
import { Typography, Input, Row, Col, Collapse, Space, Button, Card } from 'antd'
import PropTypes from 'prop-types'
import { ImageUploader, RichTextContentEditor } from '~components'

import DownArrow from '../PositionArrows/DownArrow'
import UpArrow from '../PositionArrows/UpArrow'
import styles from './FAQPageSections.module.css'

const { Title } = Typography
const { TextArea } = Input
const { Panel } = Collapse

const FAQPageSections = ({ sections, setSections }) => {
  const addSection = () => {
    setSections([...sections, { title: '', anchor: '', questions: [] }])
  }

  const deleteSection = (event, index) => {
    event.stopPropagation()
    const updated = [...sections]
    updated.splice(index, 1)
    setSections(updated)
  }

  const handleSectionTitleChange = ({ target }, index) => {
    const updated = [...sections]
    updated[index] = { ...updated[index], title: target.value }
    setSections(updated)
  }

  const handleSectionAnchorChange = ({ target }, index) => {
    const updated = [...sections]
    updated[index] = { ...updated[index], anchor: target.value }
    setSections(updated)
  }

  const addQuestion = (sectionIndex) => {
    const updated = [...sections]
    updated[sectionIndex] = {
      ...updated[sectionIndex],
      questions: [...(updated[sectionIndex].questions || []), { question: '', answer: '', image: { url: '', alt: '', caption: '' } }]
    }
    setSections(updated)
  }

  const deleteQuestion = (event, sectionIndex, qIndex) => {
    event.stopPropagation()
    const updated = [...sections]
    const questions = [...updated[sectionIndex].questions]
    questions.splice(qIndex, 1)
    updated[sectionIndex] = { ...updated[sectionIndex], questions }
    setSections(updated)
  }

  const handleQuestionChange = ({ target }, sectionIndex, qIndex) => {
    const updated = [...sections]
    const questions = [...updated[sectionIndex].questions]
    questions[qIndex] = { ...questions[qIndex], question: target.value }
    updated[sectionIndex] = { ...updated[sectionIndex], questions }
    setSections(updated)
  }

  const handleAnswerChange = (answer, sectionIndex, qIndex) => {
    const updated = [...sections]
    const questions = [...updated[sectionIndex].questions]
    questions[qIndex] = { ...questions[qIndex], answer }
    updated[sectionIndex] = { ...updated[sectionIndex], questions }
    setSections(updated)
  }

  const handleQuestionImageUpload = (url, sectionIndex, qIndex) => {
    const updated = [...sections]
    const questions = [...updated[sectionIndex].questions]
    questions[qIndex] = {
      ...questions[qIndex],
      image: { ...(questions[qIndex].image || {}), url, alt: url.split('/').pop() }
    }
    updated[sectionIndex] = { ...updated[sectionIndex], questions }
    setSections(updated)
  }

  const handleQuestionImageCaptionChange = ({ target }, sectionIndex, qIndex) => {
    const updated = [...sections]
    const questions = [...updated[sectionIndex].questions]
    questions[qIndex] = {
      ...questions[qIndex],
      image: { ...(questions[qIndex].image || {}), caption: target.value }
    }
    updated[sectionIndex] = { ...updated[sectionIndex], questions }
    setSections(updated)
  }

  const handleMoveSectionUp = (event, index) => {
    event.stopPropagation()
    if (index > 0) {
      const updated = [...sections]
      const temp = updated[index]
      updated[index] = updated[index - 1]
      updated[index - 1] = temp
      setSections(updated)
    }
  }

  const handleMoveSectionDown = (event, index) => {
    event.stopPropagation()
    if (index < sections.length - 1) {
      const updated = [...sections]
      const temp = updated[index]
      updated[index] = updated[index + 1]
      updated[index + 1] = temp
      setSections(updated)
    }
  }

  const handleMoveQuestionUp = (event, sectionIndex, qIndex) => {
    event.stopPropagation()
    if (qIndex > 0) {
      const updated = [...sections]
      const questions = [...updated[sectionIndex].questions]
      const temp = questions[qIndex]
      questions[qIndex] = questions[qIndex - 1]
      questions[qIndex - 1] = temp
      updated[sectionIndex] = { ...updated[sectionIndex], questions }
      setSections(updated)
    }
  }

  const handleMoveQuestionDown = (event, sectionIndex, qIndex) => {
    event.stopPropagation()
    const questions = sections[sectionIndex].questions || []
    if (qIndex < questions.length - 1) {
      const updated = [...sections]
      const updatedQuestions = [...updated[sectionIndex].questions]
      const temp = updatedQuestions[qIndex]
      updatedQuestions[qIndex] = updatedQuestions[qIndex + 1]
      updatedQuestions[qIndex + 1] = temp
      updated[sectionIndex] = { ...updated[sectionIndex], questions: updatedQuestions }
      setSections(updated)
    }
  }

  return (
    <div className={styles.container}>
      <Row justify="end" style={{ marginBottom: 16 }}>
        <Button onClick={addSection} type="primary">Add Section</Button>
      </Row>
      <Collapse accordion defaultActiveKey={['0']} expandIconPosition="right" style={{ width: '100%' }}>
        {(sections || []).map((section, sectionIndex) => (
          <Panel
            key={sectionIndex}
            extra={
              <Row gutter={2}>
                <Space>
                  <Col align="left" span={4}>
                    <Row gutter={[10]} style={{ width: '60px' }}>
                      {sectionIndex !== 0 && (
                        <Col align="right" onClick={(e) => handleMoveSectionUp(e, sectionIndex)} span={12}>
                          <UpArrow />
                        </Col>
                      )}
                      {sectionIndex < sections.length - 1 && (
                        <Col align="left" onClick={(e) => handleMoveSectionDown(e, sectionIndex)} span={12}>
                          <DownArrow />
                        </Col>
                      )}
                    </Row>
                  </Col>
                  <Button danger onClick={(e) => deleteSection(e, sectionIndex)} style={{ backgroundColor: '#DA0D50', color: '#fff' }}>
                    Delete Section
                  </Button>
                </Space>
              </Row>
            }
            header={<span style={{ fontSize: 20 }}>{(section && section.title) || `Section ${sectionIndex + 1}`}</span>}
          >
            <Row align="left" style={{ marginBottom: 8 }}>
              <Col><Title level={3}>Section Title*</Title></Col>
            </Row>
            <Row>
              <TextArea
                autoSize={{ minRows: 1 }}
                onChange={(e) => handleSectionTitleChange(e, sectionIndex)}
                style={{ width: '100%' }}
                value={(section && section.title) || ''}
              />
            </Row>
            <br />
            <Row align="left" style={{ marginBottom: 8 }}>
              <Col><Title level={3}>Anchor (for navigation)</Title></Col>
            </Row>
            <Row>
              <TextArea
                autoSize={{ minRows: 1 }}
                onChange={(e) => handleSectionAnchorChange(e, sectionIndex)}
                placeholder="e.g. ticketing-information"
                style={{ width: '100%' }}
                value={(section && section.anchor) || ''}
              />
            </Row>
            <br />
            <Card
              extra={<Button onClick={() => addQuestion(sectionIndex)} type="primary">Add Question</Button>}
              title="Questions & Answers"
            >
              <Collapse accordion expandIconPosition="right">
                {((section && section.questions) || []).map((item, qIndex) => (
                  <Panel
                    key={qIndex}
                    extra={
                      <Space>
                        {qIndex > 0 && (
                          <Col onClick={(e) => handleMoveQuestionUp(e, sectionIndex, qIndex)}>
                            <UpArrow />
                          </Col>
                        )}
                        {qIndex < ((section.questions && section.questions.length) || 0) - 1 && (
                          <Col onClick={(e) => handleMoveQuestionDown(e, sectionIndex, qIndex)}>
                            <DownArrow />
                          </Col>
                        )}
                        <Button danger onClick={(e) => deleteQuestion(e, sectionIndex, qIndex)} style={{ backgroundColor: '#DA0D50', color: '#fff' }}>
                          Delete
                        </Button>
                      </Space>
                    }
                    header={<span>{(item && item.question) || `Question ${qIndex + 1}`}</span>}
                  >
                    <Row align="left" style={{ marginBottom: 8 }}>
                      <Col><Title level={4}>Question*</Title></Col>
                    </Row>
                    <Row>
                      <TextArea
                        autoSize={{ minRows: 1 }}
                        onChange={(e) => handleQuestionChange(e, sectionIndex, qIndex)}
                        style={{ width: '100%' }}
                        value={(item && item.question) || ''}
                      />
                    </Row>
                    <br />
                    <Row align="left" style={{ marginBottom: 8 }}>
                      <Col><Title level={4}>Answer</Title></Col>
                    </Row>
                    <Row style={{ minHeight: 120, height: '100%', width: '100%' }}>
                      <RichTextContentEditor
                        onChange={(answer) => handleAnswerChange(answer, sectionIndex, qIndex)}
                        value={(item && item.answer) || ''}
                      />
                    </Row>
                    <br />
                    <Row align="left" style={{ marginBottom: 8 }}>
                      <Col><Title level={4}>Image</Title></Col>
                    </Row>
                    <ImageUploader
                      handleUpload={(url) => handleQuestionImageUpload(url, sectionIndex, qIndex)}
                      logoUrl={item && item.image && item.image.url}
                      pageType='faq'
                    />
                    {item && item.image && item.image.url && (
                      <>
                        <br />
                        <Row align="left" style={{ marginBottom: 8 }}>
                          <Col><Title level={5}>Image Caption</Title></Col>
                        </Row>
                        <Row>
                          <TextArea
                            autoSize={{ minRows: 1 }}
                            onChange={(e) => handleQuestionImageCaptionChange(e, sectionIndex, qIndex)}
                            style={{ width: '100%' }}
                            value={(item && item.image && item.image.caption) || ''}
                          />
                        </Row>
                      </>
                    )}
                  </Panel>
                ))}
              </Collapse>
            </Card>
          </Panel>
        ))}
      </Collapse>
    </div>
  )
}

FAQPageSections.propTypes = {
  sections: PropTypes.array,
  setSections: PropTypes.func.isRequired
}

FAQPageSections.defaultProps = {
  sections: []
}

export default FAQPageSections
